import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

export const ROUTE_ICON_SIZES = ['md', 'lr', 'xl'] as const;
export const ROUTE_ICON_TEXT_SIZES = ['xs', 'sm', 'md', 'lg'] as const;

export type RouteIconSize = (typeof ROUTE_ICON_SIZES)[number];
export type RouteIconSizeInput = RouteIconSize | `${number}rem`;
export type RouteIconTextSize = (typeof ROUTE_ICON_TEXT_SIZES)[number];

@Component({
    selector: 'rwidget-route-short-name',
    templateUrl: './route-short-name.component.html',
    styleUrls: ['./route-short-name.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RouteShortNameComponent implements OnChanges {
    @Input() label: string;
    @Input() color: string;
    @Input() textColor: string;
    @Input() size: RouteIconSizeInput = 'md';
    @Input() textSize: RouteIconTextSize = 'sm';
    @Input() disabledColor = '#838383';
    @Input() isDisabled = false; // disabled === not selected
    @Input() shape: string;
    @Input() isSelected = true;

    resolvedTextColor: string;
    backgroundColor: string;
    borderColor: string;
    resolvedSize = '1.75rem';

    get textSizeClass(): string {
        switch (this.textSize) {
            case 'xs':
                return 'text-xs';
            case 'sm':
                return 'text-sm';
            case 'md':
                return 'text-base';
            case 'lg':
                return 'text-lg';
            default:
                return 'text-sm';
        }
    }

    ngOnChanges() {
        this.resolvedTextColor = this.resolveTextColor();
        this.backgroundColor = this.resolveBackGroundColor();
        this.borderColor = this.resolveBorderColor();
        this.resolvedSize = this.resolveSize(this.size);
    }

    resolveSize(size: RouteIconSizeInput): string {
        // If a custom rem value is provided (e.g., '2.625rem'), use it directly
        if (size.includes('rem')) {
            return size;
        }

        switch (size) {
            case 'md':
                return `1.75rem`; // 28px
            case 'lr':
                return `2.06rem`; // 33px
            case 'xl':
                return `2.625rem`; // 42px
            default:
                return `1.75rem`; // 28px
        }
    }

    resolveBorderColor(): string {
        if (this.isDisabled) {
            return this.resolveDisabledColor();
        }

        return this.getColor(this.color);
    }

    resolveBackGroundColor() {
        if (this.isDisabled) {
            return 'transparent';
        }
        return this.getColor(this.color);
    }

    resolveTextColor() {
        if (this.isDisabled) {
            return this.resolveDisabledColor();
        }

        const resolved = this.textColor ? this.getColor(this.textColor) : '#FFFFFF';
        const bg = this.getColor(this.color);
        // If text color is the same as background color, default to white
        if (resolved && bg && resolved === bg) {
            switch (bg?.toLowerCase()) {
                case '#ffffff':
                    return '#000000';
                case '#000000':
                    return '#ffffff';
                default:
                    return '#ffffff';
            }
        }
        return resolved;
    }

    private resolveDisabledColor(): string {
        const color = this.disabledColor?.trim();
        return color ? this.getColor(color) : '#838383';
    }

    getColor(color) {
        const hexColor6 = /^#?[0-9A-Fa-f]{6}$/;
        const hexColor3 = /^#?[0-9A-Fa-f]{3}$/;

        if ((hexColor6.test(color) || hexColor3.test(color)) && !color.startsWith('#')) {
            return `#${color}`;
        }

        return color;
    }
}
